import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { TelSysConfiguration } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty, isStringNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { useFormContext, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { getTimezoneDefault, getTimezoneOptions } from '../../Common/API/UtilitiesAPI'
import FormRow from '../../Common/Components/FormRow'
import { SelectOptions } from '../../Common/Components/Selects'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { RecordingIntegrations } from '../../Common/Constants/Recordings'
import { ReactState } from '../../Common/Contexts'
import LanguageContext from '../../Common/Contexts/LanguageContext'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { ALL_TELSYS_ID, TelSysTypes } from '../Constants/Constants'
import {
    COUNTRY_CODE_FIELD_NAME,
    E164_ENABLED_FIELD_NAME,
    INTERNAL_DIGIT_LENGTH_FIELD_NAME,
    OUTBOUND_TEL_NO_PREFIX_FIELD_NAME,
    RECORDING_INTEGRATION_FIELD_NAME,
    TIMEZONE_FIELD_NAME,
    TYPE_FIELD_NAME
} from '../Constants/FieldNames'
import { TelSysForm } from '../Types/Types'
import { CallRecordingIntegration } from '../../../NonReact/Types/Types'
import { SelectOption } from '../../Common/Components/Selects/Types'
import { useLicenceFeatureFlag } from '../../Common/Hooks/useLicenceFeatureFlag'
import { CALL_RECORDING_NAME } from '../../AddUser/FieldNames'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { Rules } from '../../Common/Rules/Rules'
import { OutlinedSelect } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedSelect'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'
import { getCallRecordingIntegration } from '../../../NonReact/common/API/EntityAPIClient'
import { JSONResponse } from '../../../NonReact/common/Types/Types'

type Props = {
    selectedTelSys?: string
    viewOnly?: boolean
}

export const DefaultSiteSettingsTab: FC<Props> = ({ selectedTelSys, viewOnly }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const { displayError }: MessageContextType = useContext(MessageContext)

    const { watch, setValue }: UseFormMethods<TelSysForm> = useFormContext()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const isCallRecordingEnabled: Nullable<boolean> = useLicenceFeatureFlag('FeatureCapsCallRecIntegration')

    const [availableTimezones, setAvailableTimezones]: ReactState<SelectOptions<string>> = useState<
        SelectOptions<string>
    >([])
    const [availableCallRecordingIntegrations, setAvailableCallRecordingIntegrations]: ReactState<
        SelectOptions<string>
    > = useState<SelectOptions<string>>([
        {
            label: language.get(TelSysConfiguration.TelSysConfiguration_NoneSelected),
            value: RecordingIntegrations.NONE
        }
    ])

    const telSysTypeValue: string = watch(TYPE_FIELD_NAME)
    const isE164Enabled: boolean = watch(E164_ENABLED_FIELD_NAME)

    const shouldShowCountryCode: boolean = useMemo((): boolean => {
        if (telSysTypeValue === TelSysTypes.BROADSOFT_BROADWORKS || telSysTypeValue === TelSysTypes.WEBEX_CALLING) {
            return true
        }

        if (telSysTypeValue === TelSysTypes.MS_TEAMS && !isE164Enabled) {
            return true
        }

        return false
    }, [telSysTypeValue, isE164Enabled])

    useEffect((): void => {
        getTimezoneOptions()
            .then((timezones: SelectOptions<string>): void => {
                if (isMounted.current) {
                    setAvailableTimezones(timezones)
                }
            })
            .catch(displayError)
    }, [])

    useEffect((): void => {
        if (IsStringEmpty(selectedTelSys)) {
            getTimezoneDefault()
                .then((defaultTimezone: string): void => {
                    if (isMounted.current) {
                        setValue(TIMEZONE_FIELD_NAME, defaultTimezone)
                    }
                })
                .catch(displayError)
        }
    }, [selectedTelSys])

    useEffect((): void => {
        if (isCallRecordingEnabled === true) {
            getCallRecordingIntegration(isStringNotEmpty(selectedTelSys) ? selectedTelSys : ALL_TELSYS_ID)
                .then((callRecordings: JSONResponse<CallRecordingIntegration[]>): void => {
                    if (isMounted.current) {
                        setAvailableCallRecordingIntegrations([
                            {
                                label: language.get(TelSysConfiguration.TelSysConfiguration_NoneSelected),
                                value: RecordingIntegrations.NONE
                            },
                            ...(callRecordings?.map(
                                (callRecordingIntegration: CallRecordingIntegration): SelectOption<string> => ({
                                    label: callRecordingIntegration.Description,
                                    value: callRecordingIntegration.ID
                                })
                            ) ?? [])
                        ])
                    }
                })
                .catch(displayError)
        } else if (isCallRecordingEnabled === false) {
            setValue(CALL_RECORDING_NAME, EMPTY_STRING)
        }
    }, [isCallRecordingEnabled])

    return (
        <div className='default-site-settings-tab tab-area'>
            <FormRow>
                <ValidationErrors name={TIMEZONE_FIELD_NAME}>
                    <OutlinedSelect
                        name={TIMEZONE_FIELD_NAME}
                        label={language.get(TelSysConfiguration.TelSysConfiguration_Field_Timezone_Label)}
                        options={availableTimezones}
                        disabled={viewOnly}
                    />
                </ValidationErrors>
            </FormRow>
            {telSysTypeValue === TelSysTypes.MS_TEAMS ? (
                <FormRow>
                    <RouterCheckbox
                        name={E164_ENABLED_FIELD_NAME}
                        labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_E164Enabled_Label)}
                        disabled={viewOnly}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {shouldShowCountryCode ? (
                <FormRow>
                    <ValidationErrors name={COUNTRY_CODE_FIELD_NAME}>
                        <OutlinedTextInput
                            inputName={COUNTRY_CODE_FIELD_NAME}
                            labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_CountryCode_Label)}
                            disabled={viewOnly}
                            rules={{
                                validate: {
                                    isNumber: Rules.isNumber(language)
                                }
                            }}
                        />
                    </ValidationErrors>
                </FormRow>
            ) : (
                <></>
            )}
            {telSysTypeValue === TelSysTypes.BROADSOFT_BROADWORKS ||
            telSysTypeValue === TelSysTypes.WEBEX_CALLING ||
            telSysTypeValue === TelSysTypes.BROADSOFT_M6 ? (
                <FormRow>
                    <ValidationErrors name={INTERNAL_DIGIT_LENGTH_FIELD_NAME}>
                        <OutlinedTextInput
                            inputName={INTERNAL_DIGIT_LENGTH_FIELD_NAME}
                            labelLeft={language.get(
                                TelSysConfiguration.TelSysConfiguration_Field_InternalDigitLength_Label
                            )}
                            disabled={viewOnly}
                            rules={{
                                validate: {
                                    isNumber: Rules.isNumber(language)
                                }
                            }}
                        />
                    </ValidationErrors>
                </FormRow>
            ) : (
                <></>
            )}
            {!isE164Enabled || telSysTypeValue !== TelSysTypes.MS_TEAMS ? (
                <FormRow>
                    <ValidationErrors name={OUTBOUND_TEL_NO_PREFIX_FIELD_NAME}>
                        <OutlinedTextInput
                            inputName={OUTBOUND_TEL_NO_PREFIX_FIELD_NAME}
                            labelLeft={language.get(
                                TelSysConfiguration.TelSysConfiguration_Field_OutboundTelNoPrefix_Label
                            )}
                            disabled={viewOnly}
                            rules={{
                                validate: {
                                    isNumber: Rules.isNumber(language)
                                }
                            }}
                        />
                    </ValidationErrors>
                </FormRow>
            ) : (
                <></>
            )}
            {telSysTypeValue !== TelSysTypes.MS_TEAMS && isCallRecordingEnabled ? (
                <FormRow>
                    <ValidationErrors name={RECORDING_INTEGRATION_FIELD_NAME}>
                        <OutlinedSelect
                            name={RECORDING_INTEGRATION_FIELD_NAME}
                            options={availableCallRecordingIntegrations}
                            label={language.get(
                                TelSysConfiguration.TelSysConfiguration_Field_CallRecordingIntegration_Label
                            )}
                            disabled={viewOnly}
                        />
                    </ValidationErrors>
                </FormRow>
            ) : (
                <></>
            )}
        </div>
    )
}
